import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';
import { GREEN_COLOR } from '../constants';
import {
  Section,
  SectionContent,
  SectionTitle,
  SectionSubtitle,
  SectionDescription,
} from '../styles/sections';
import { CtaButton } from '../styles/hero';
import {
  SignupContainer,
  SignupImage,
  SignupForm,
  InputWrapper,
  Input,
  Label,
  ErrorMessage,
} from '../styles/signup';

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateField = (name, value) => {
    if (name === 'name')
      return !value.trim() ? 'Name is required' : value.trim().length < 2 ? 'Name too short' : '';
    if (name === 'email')
      return !value ? 'Email is required' : !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email' : '';
    if (name === 'message')
      return !value.trim()
        ? 'Message is required'
        : value.trim().length < 10
        ? 'Please add a bit more detail'
        : '';
    return '';
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      message: validateField('message', formData.message),
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.message;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors');
      return;
    }

    setIsLoading(true);

    const templateParams = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      timestamp: new Date().toLocaleString(),
    };

    // NOTE: uses a separate EmailJS template so contact enquiries don't land
    // in the same inbox flow as newsletter signups. Add
    // REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID to your .env — it can point at
    // the same EmailJS service, just a different template.
    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID
      )
      .then(() => {
        toast.success("Message sent — we'll get back to you soon");
        setFormData({ name: '', email: '', message: '' });
        setErrors({});
        setIsSubmitted(true);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('EmailJS Error:', err);
        toast.error('Failed to send. Try again or email us directly.');
        setIsLoading(false);
      });
  };

  return (
    <Section id="contact" backgroundColor="#fff">
      <SectionContent>
        <SectionSubtitle style={{ color: '#000' }}>Get In Touch</SectionSubtitle>
        <SectionTitle style={{ textAlign: 'left' }}>Contact Us</SectionTitle>
        <SectionDescription style={{ textAlign: 'left' }}>
          Got a question, feedback, or partnership idea? Send us a message and we'll get
          back to you.
        </SectionDescription>

        <SignupContainer>
          <SignupImage src="/bottom-pic.jpg" alt="Get in touch with Playfair" loading="lazy" />

          {!isSubmitted ? (
            <SignupForm onSubmit={handleSubmit}>
              <InputWrapper>
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  disabled={isLoading}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </InputWrapper>

              <InputWrapper>
                <Label htmlFor="email">Your Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  disabled={isLoading}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </InputWrapper>

              <InputWrapper>
                <Label htmlFor="message">Message</Label>
                <Input
                  as="textarea"
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  disabled={isLoading}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </InputWrapper>

              <CtaButton type="submit" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Message'}
              </CtaButton>
            </SignupForm>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ fontSize: '1.6rem', fontWeight: '700', color: GREEN_COLOR }}
            >
              Thanks, {formData.name}! We'll be in touch shortly.
            </motion.div>
          )}
        </SignupContainer>
      </SectionContent>
    </Section>
  );
}

export default Contact;
